import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, X, Loader2, User as UserIcon, CheckCircle, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import useToken from "../../../../hooks/auth/useToken";
import { SearchedUser, WalletActionData } from "../../../../types/features/administration/Wallet";
import { Country } from "../../../../types/features/administration/User";
import { usersService } from "../../../../services/administration/usersService";

interface RechargeModalProps {
  countries: Country[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: WalletActionData) => void;
  isLoading: boolean;
  isDebit?: boolean;
  isCreate?: boolean;
}

export const RechargeModal: React.FC<RechargeModalProps> = ({
  countries,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isDebit = false,
  isCreate = false,
}) => {
  const { token } = useToken();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchedUser | null>(null);
  const [form, setForm] = useState({ country_id: 0, amount: "", description: "" });
  const [errors, setErrors] = useState<{
    user?: string;
    country_id?: string;
    amount?: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Devise sélectionnée
  const selectedCountry = countries.find((c) => c.id === form.country_id);
  const devise = selectedCountry?.devise || "";

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUser(null);
      setForm({ country_id: 0, amount: "", description: "" });
      setErrors({});
      setSuccessMessage("");
      setShowDropdown(false);
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced user search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setSelectedUser(null);
      if (errors.user) setErrors((prev) => ({ ...prev, user: undefined }));

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      if (value.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const results = await usersService.searchUsers(token, value);
          setSearchResults(results as any);
          setShowDropdown(results.length > 0);
        } catch {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 400);
    },
    [token, errors.user]
  );

  // Select user from dropdown
  const handleSelectUser = (user: SearchedUser) => {
    setSelectedUser(user);
    setSearchQuery(`${user.first_name || ""} ${user.last_name || ""} (${user.email})`);
    setShowDropdown(false);

    // Auto-select country if user has one
    if (user.country?.id) {
      setForm((prev) => ({ ...prev, country_id: user.country!.id }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { user?: string; country_id?: string; amount?: string } = {};
    if (!selectedUser) newErrors.user = "Veuillez sélectionner un utilisateur";
    if (!form.country_id) newErrors.country_id = "Le pays est requis";
    if (!form.amount || Number(form.amount) <= 0) newErrors.amount = "Le montant doit être supérieur à 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && selectedUser) {
      onConfirm({
        user_id: selectedUser.id,
        country_id: form.country_id,
        amount: Number(form.amount),
        description: form.description || undefined,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            {isDebit ? (
              <>
                <ArrowDownCircle className="w-6 h-6 text-red-600" /> Débiter un Wallet
              </>
            ) : (
              <>
                <ArrowUpCircle className="w-6 h-6 text-green-600" /> Recharger un Wallet
              </>
            )}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Recherche utilisateur */}
          <div ref={searchRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Rechercher un utilisateur *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.user ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Tapez un nom, email ou téléphone..."
                disabled={isLoading}
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
              {selectedUser && !isSearching && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {errors.user && <p className="mt-1 text-sm text-red-600">{errors.user}</p>}

            {/* Dropdown résultats */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className="w-full px-4 py-3 text-left hover:bg-green-50 flex items-center gap-3 border-b border-gray-100 last:border-0 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <UserIcon className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.first_name || ""} {user.last_name || ""}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      {user.phonenumber && <p className="text-xs text-gray-400">{user.phonenumber}</p>}
                    </div>
                    {user.country && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded flex-shrink-0">
                        {user.country.code}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {showDropdown && searchResults.length === 0 && !isSearching && searchQuery.length >= 2 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
                Aucun utilisateur trouvé pour "{searchQuery}"
              </div>
            )}
          </div>

          {/* Utilisateur sélectionné */}
          {selectedUser && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-green-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-green-900">
                  {selectedUser.first_name || ""} {selectedUser.last_name || ""}
                </p>
                <p className="text-xs text-green-700 truncate">{selectedUser.email}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedUser(null);
                  setSearchQuery("");
                  setForm((prev) => ({ ...prev, country_id: 0 }));
                }}
                className="text-green-600 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Pays / Devise */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pays (Devise) *</label>
            <select
              value={form.country_id}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, country_id: Number(e.target.value) }));
                if (errors.country_id) setErrors((prev) => ({ ...prev, country_id: undefined }));
              }}
              className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${
                errors.country_id ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isLoading}
            >
              <option value={0}>-- Sélectionner un pays --</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code}) - {c.devise}
                </option>
              ))}
            </select>
            {errors.country_id && <p className="mt-1 text-sm text-red-600">{errors.country_id}</p>}
          </div>

          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, amount: e.target.value }));
                  if (errors.amount) setErrors((prev) => ({ ...prev, amount: undefined }));
                }}
                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  devise ? "pr-16" : ""
                } ${errors.amount ? "border-red-300" : "border-gray-300"}`}
                placeholder="Ex: 5000"
                disabled={isLoading}
              />
              {devise && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  {devise}
                </span>
              )}
            </div>
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Ex: Recharge bonus..."
              disabled={isLoading}
            />
          </div>

          {/* Résumé avant confirmation */}
          {selectedUser && form.country_id > 0 && Number(form.amount) > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
              <p className="font-medium text-gray-700">{isDebit ? "📤 Résumé du débit" : "📥 Résumé de la recharge"}</p>
              <p className="text-gray-600">
                <span className="font-medium">Utilisateur :</span> {selectedUser.first_name} {selectedUser.last_name}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Montant :</span>{" "}
                <span className={`font-bold ${isDebit ? "text-red-600" : "text-green-600"}`}>
                  {Number(form.amount).toLocaleString()} {devise}
                </span>
              </p>
            </div>
          )}

          {/* Message de succès */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {successMessage}
            </div>
          )}

          {/* Boutons */}
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`flex-1 text-white px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 font-medium ${
                isDebit ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
              }`}
              disabled={isLoading || !selectedUser}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isDebit ? "Débit en cours..." : "Recharge en cours..."}</span>
                </>
              ) : (
                <>
                  {isDebit ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                  <span>{isDebit ? "Débiter" : "Recharger"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
